import React, { useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import BoringAvatar from "boring-avatars";
import { AlertCircle, Copy, MoreHorizontal, Trash2 } from "lucide-react";
import { mutate } from "swr";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Switch } from "~/components/ui/switch";
import useToast from "~/components/ui/toast";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useCollapseSideNav } from "~/contexts/nextgen-sidenav";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { useCanManageAIAgents } from "~/pages/manifold-nextgen/hooks/useCanManageAIAgents";
import { useDuplicateAgent } from "~/pages/manifold-nextgen/hooks/useDuplicateAgent";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import {
  AGENT_BRAND_COLORS,
  Agent,
  AgentScope,
  AgentStatus,
  agentListPath,
  agentPath,
  deleteAgent,
  updateAgent,
} from "~/services/agents";
import { DeleteAgentDialog } from "./DeleteAgentDialog";

const NAV_ITEMS = [
  { label: "Profile", path: "profile" },
  { label: "Skills", path: "skills" },
  { label: "Toolkits", path: "toolkits" },
  { label: "Connections", path: "connections" },
  { label: "Conversations", path: "conversations" },
] as const;

export const AgentDetail = () => {
  useCollapseSideNav();
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { data: agent, error, isLoading } = useFetcher<Agent>(agentPath(agentId!));
  const canManage = useCanManageAIAgents();
  const { showSuccess, showError } = useToast();
  const duplicateAgentAction = useDuplicateAgent();

  const [enableConfirmOpen, setEnableConfirmOpen] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isEditable = !!agent && agent.scope !== AgentScope.SYSTEM;

  const headerBreadcrumbs = useMemo(
    () => [
      { label: "All Agents", href: `${ROUTE_NEXTGEN_VIEW}/agents` },
      ...(agent ? [{ label: agent.display_name }] : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [agent?.display_name]
  );

  const headerActions = useMemo(
    () =>
      [
        <Button key="share" variant="outline" size="sm" className="text-xs" disabled>
          Share
        </Button>,
      ] as [React.ReactNode?, React.ReactNode?],
    []
  );

  useNextGenHeader({ breadcrumbs: headerBreadcrumbs, actions: headerActions });

  const doToggle = async (status: AgentStatus) => {
    setIsToggling(true);
    try {
      await updateAgent(agentId!, { status });
      await Promise.all([mutate(agentPath(agentId!)), mutate(agentListPath())]);
      showSuccess(status === AgentStatus.PUBLISHED ? "Agent enabled" : "Agent disabled");
    } catch {
      showError("Failed to update agent status");
    } finally {
      setIsToggling(false);
      setEnableConfirmOpen(false);
    }
  };

  const handleToggleStatus = (checked: boolean) => {
    if (checked) {
      setEnableConfirmOpen(true);
    } else {
      doToggle(AgentStatus.DRAFT);
    }
  };

  const handleDuplicate = () => duplicateAgentAction(agentId!, agent!.display_name);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteAgent(agentId!);
      await mutate(agentListPath());
      showSuccess(`"${agent!.display_name}" deleted`);
      navigate(`${ROUTE_NEXTGEN_VIEW}/agents`);
    } catch {
      showError("Failed to delete agent");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-full">
        <div className="w-48 shrink-0 border-r border-border p-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="mx-1 my-1 h-8 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
        <div className="flex-1 p-6">
          <div className="h-64 animate-pulse rounded-lg bg-muted" />
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
      >
        <AlertCircle className="size-8" aria-hidden="true" />
        <p className="text-sm font-medium">Failed to load agent</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex min-h-full">
        <nav
          aria-label="Agent sections"
          className="flex w-56 shrink-0 flex-col border-r border-border"
        >
          <div className="relative flex flex-col gap-3 border-b border-border px-3 py-4">
            {canManage && (
              <div className="absolute right-2 top-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-6 text-muted-foreground focus-visible:border-transparent focus-visible:ring-0"
                      aria-label={`Actions for ${agent.display_name}`}
                    >
                      <MoreHorizontal />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={handleDuplicate}>
                      <Copy />
                      Duplicate
                    </DropdownMenuItem>
                    {isEditable && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
                          <Trash2 />
                          Delete
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            <div className="flex items-center gap-3">
              <div className="size-12 shrink-0 overflow-hidden rounded-full">
                <BoringAvatar
                  size={48}
                  name={agent.name}
                  variant="beam"
                  colors={AGENT_BRAND_COLORS}
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">{agent.display_name}</p>
                {agent.scope === AgentScope.SYSTEM && (
                  <p className="text-xs text-muted-foreground">System · Read-only</p>
                )}
              </div>
            </div>

            {canManage && (
              <>
                <div className="-mx-3 border-t border-border" />
                <div className="flex items-center justify-between mx-3">
                  <span className="text-sm font-medium text-muted-foreground">
                    {agent.status === AgentStatus.PUBLISHED ? "Enabled" : "Disabled"}
                  </span>
                  <Switch
                    checked={agent.status === AgentStatus.PUBLISHED}
                    onCheckedChange={handleToggleStatus}
                    disabled={isToggling}
                    aria-label="Enable agent"
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex flex-col gap-1 p-3">
            {NAV_ITEMS.map(({ label, path }) => (
              <NavLink
                key={path}
                to={path}
                end
                className={({ isActive }) =>
                  cn(
                    "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                  )
                }
              >
                {label}
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="flex-1 p-6">
          <Outlet context={agent} />
        </div>
      </div>

      <AlertDialog open={enableConfirmOpen} onOpenChange={setEnableConfirmOpen}>
        <AlertDialogContent data-theme="manifold-nextgen">
          <AlertDialogHeader>
            <AlertDialogTitle>Enable &ldquo;{agent.display_name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This will make the agent available to users.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isToggling}>Cancel</AlertDialogCancel>
            <Button onClick={() => doToggle(AgentStatus.PUBLISHED)} disabled={isToggling}>
              {isToggling ? "Enabling…" : "Enable"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DeleteAgentDialog
        agent={agent}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  );
};
